<script lang="ts">
import type { EventBusKey } from '@vueuse/core'
import { breakpointsTailwind } from '@vueuse/core'
import type { Component } from 'vue'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { useLayout } from '../composables/layout'
import { LayoutSections } from './sections'
import { useLeftSidebarStore } from '~~/src/stores/leftSidebar'
import { useSidebarStore } from '~~/src/stores/sidebar'
import AppHeaderMenu from '@/components/App/Header/Menu/index.vue'
import AppHeaderMenuSaved from '@/components/App/Header/Menu/Saved/index.vue'
import CashOut from '@/pages/marketplace/dashboard/@components/CashOut/index.vue'
export const CONTAINER_MAX_WIDTH = 1400 // PX
export const MAIN_SCROLL_MIN_WIDTH = 375 // PX

type Overlay = 'menu' | 'saved' | 'cashOut'
type EventBusOverlayEvent = `toggle-${Overlay}` | 'close-overlay'

export type EventBusEvent =
  | EventBusOverlayEvent
  | 'reach-end'
  | 'comment:cancel-forms'
  | 'focus-buyPromptPriceInput'
  | 'expand-navigation'
  | 'collapse-navigation'
const eventBusKey = Symbol('layout:default') as EventBusKey<EventBusEvent>

export const bus = useEventBus<EventBusEvent, Component | undefined>(
  eventBusKey,
)
</script>

<script setup lang="ts">
const router = useRouter()
// const localType = useStorage(
//   PROMPT_TYPE_STORAGE_KEY,
//   PROMPT_TYPE_STORAGE_DEFAULT,
// )
const { mobileHeaderHeight } = useLayout()
const globalScroll = useGlobalScroll()
const route = useRoute()

const showExtensionBanner = useShowExtensionBanner()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isDesktop = breakpoints.greaterOrEqual('lg')
const showOverlay = ref(false)

const sidebarRightStore = useSidebarStore()
const sidebarRight = sidebarRightStore.state
const toggleSidebarRight = sidebarRightStore.toggleSidebar

const sidebarLeftStore = useLeftSidebarStore()
const toggleSidebarLeft = sidebarLeftStore.toggleSidebar

const overlayToShow = ref<Overlay | null>(null)
const overlayComponent = shallowRef<Component | null>(null)

const { isSmaller } = useBreakpoints(breakpointsTailwind)
const isMobile = ref(isSmaller('md'))

if (
  !process.server
) {
  useResizeObserver(window.document.body, () => {
    isMobile.value = isSmaller('md')
  })
}

watch(route, () => {
  const regex = /^\/(api-keys|my-teams|team)(\/.*)?$/

  if (!process.server) {
    if (route.meta.noBackgroundColor) {
      document?.querySelector('body')?.classList.remove('md:bg-gray-lighter')
      return
    }
    if (regex.test(route.path))
      document?.querySelector('body')?.classList.add('md:bg-gray-lighter')
    else
      document?.querySelector('body')?.classList.remove('md:bg-gray-lighter')
  }
},
{ immediate: true },
)

// watch(localType, () => {
//   if (globalScroll.scrollTop)
//     globalScroll.scrollTop()
// })

watch(showOverlay, () => {
  if (globalScroll.scrollLock)
    globalScroll.scrollLock.value = showOverlay.value
})

watch(overlayToShow, () => {
  if (!overlayToShow.value)
    return null

  const overlayComponents: Record<Overlay, Component> = {
    menu: AppHeaderMenu,
    saved: AppHeaderMenuSaved,
    cashOut: CashOut,
  }

  return (overlayComponent.value = overlayComponents[overlayToShow.value])
})
router.beforeEach(() => {
  showOverlay.value = false
})

function toggleOverlay(overlay: Overlay) {
  showOverlay.value = false
  if (overlayToShow.value === overlay) {
    overlayToShow.value = null
    return
  }

  const timeout = overlayToShow.value ? 125 : 0

  setTimeout(() => {
    overlayToShow.value = overlay
    showOverlay.value = true
  }, timeout)
}

bus.on((e, payload) => {
  const overlayHandlers: Record<EventBusOverlayEvent, () => void> = {
    'toggle-menu': () => toggleOverlay('menu'),
    'toggle-saved': () => toggleOverlay('saved'),
    'toggle-cashOut': () => toggleOverlay('cashOut'),
    'close-overlay': () => {
      showOverlay.value = false
      overlayToShow.value = null
    },
  }

  if (payload) {
    overlayComponent.value = payload
    showOverlay.value = true
  }
  else if (e in overlayHandlers) {
    overlayHandlers[e as EventBusOverlayEvent]()
  }

  if (isMobile.value) {
    setTimeout(() => {
      if (sidebarRight.isOpen)
        toggleSidebarRight()
      if (sidebarLeftStore.isOpen)
        toggleSidebarLeft()
    }, 150)
  }
})

const onInfiniteScroll = () => {
  bus.emit('reach-end')
}

const onWindowResize = () => {
  if (isDesktop.value) {
    showOverlay.value = false
    if (globalScroll.scrollLock)
      globalScroll.scrollLock.value = false
  }
}

const isDev = import.meta.env.DEV
</script>

<template>
  <main max-h-screen h="100dvh" overflow-hidden flex flex-col>
    <header class="sticky top-0 z-40">
      <slot name="drawer" />

      <ClientOnly>
        <AppHeaderBanner v-show="showExtensionBanner" />
      </ClientOnly>
      <slot name="header">
        <AppHeader z-10 />
      </slot>
    </header>

    <div flex w-full overflow-hidden self-stretch h-full items-stretch class="<md:bg-white">
      <slot name="root-body-container">
        <DrawerSidebarLeft />

        <AppScroll
          @infinite-scroll="onInfiniteScroll"
          @window-resize="onWindowResize"
        >
          <div
            id="main-scroll"
            class="overflow-x-hidden w-full overflow-y-scroll flex flex-col relative"
            :class="$style['hidde-scroll']"
            :style="{ minWidth: `${MAIN_SCROLL_MIN_WIDTH}px` }"
          >
            <slot name="body-content">
              <div bg-white class="flex-1 relative">
                <slot name="top-content" />
                <slot name="outer-container">
                  <div
                    relative
                    flex
                    w-full
                    mx-auto
                    pb="10px md:5"
                    pt="5 md:10"
                    px-4 md:px-6 lg:px-8
                    bg-white
                    :style="{
                      maxWidth: `${CONTAINER_MAX_WIDTH}px`,
                      paddingBottom: '62px',
                    }"
                  >
                    <main flex-1 w-full pb-16 class="md:w-[calc(100%-352px)]">
                      <slot />
                    </main>
                  </div>
                </slot>
              </div>
            </slot>
            <div :id="LayoutSections.FLOAT_STACK_MAIN" class="sticky bottom-0 right-0 w-full z-99" />
          </div>
        </AppScroll>

        <ClientOnly>
          <UtilsShowOnAuth>
            <LazyDrawerSidebarRight />
          </UtilsShowOnAuth>
        </ClientOnly>
      </slot>
    </div>

    <div :id="LayoutSections.FLOAT_STACK" class="flex flex-col-reverse sticky  w-full z-99 right-0 bottom-0 isolate ">
      <MobileNav />
    </div>
  </main>

  <div>
    <TransitionSlide direction="down">
      <div
        v-show="showOverlay"
        fixed
        bottom-0
        left-0
        w-full
        z-999
        center
        :style="{
          height: `calc(100dvh - ${mobileHeaderHeight})`,
        }"
      >
        <Component :is="overlayComponent" />
      </div>
    </TransitionSlide>
  </div>

  <Teleport to="body">
    <ClientOnly>
      <VueQueryDevtools v-if="isDev" />
    </ClientOnly>
    <slot name="body" />
  </Teleport>

  <AppAdminToggle />

  <ClientOnly>
    <LandingPagesDialogs />
  </ClientOnly>
</template>

<style lang="scss" module>
.hidde-scroll {
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
